import { createReducer, on } from '@ngrx/store';
import {
  CustomOrderHistory,
  CustomOrderHistoryOverview,
  CustomOrderHistoryFilterAndSortData,
} from 'src/app/uig-my-account/custom-order-history/custom-order-history.models';
import * as CustomOrderHistoryActions from './custom-order-history.actions';

export const customOrderHistoryFeatureKey = 'customOrderHistory';

export const CUSTOM_ORDER_HISTORY_OVERVIEW = 'overview';
export const CUSTOM_ORDER_HISTORY_OVERVIEW_FILTERS = 'customOrderHistoryFilters';
export const CUSTOM_ORDER_HISTORY = 'order';
export const CUSTOM_ORDER_HISTORY_CANCEL_ORDER = 'cancel-order';
export const CUSTOM_ORDER_HISTORY_CANCEL_ORDER_ENTRY = 'cancel-order-entry';
export const CUSTOM_ORDER_HISTORY_UPDATE_ORDER_ENTRIES = 'update-order-entries';

export interface CustomOrderHistoryState {
  [CUSTOM_ORDER_HISTORY_OVERVIEW]: {
    overview: CustomOrderHistoryOverview;
    isLoading: boolean;
    error?: Error;
  };
  [CUSTOM_ORDER_HISTORY_OVERVIEW_FILTERS]: {
    customOrderHistoryRequest?: CustomOrderHistoryFilterAndSortData;
    backToOverview?: boolean;
  };
  [CUSTOM_ORDER_HISTORY]: {
    order: CustomOrderHistory;
    isLoading: boolean;
    error?: Error;
  };
  [CUSTOM_ORDER_HISTORY_CANCEL_ORDER]: {
    isRunning: boolean;
    success: boolean;
    error?: boolean;
  };
  [CUSTOM_ORDER_HISTORY_CANCEL_ORDER_ENTRY]: {
    isRunning: boolean;
    success: boolean;
    error?: boolean;
  };
  [CUSTOM_ORDER_HISTORY_UPDATE_ORDER_ENTRIES]: {
    isRunning: boolean;
    success: boolean;
    error?: boolean;
  };
}

//******************************************************

export const initialState: CustomOrderHistoryState = {
  [CUSTOM_ORDER_HISTORY_OVERVIEW]: {
    overview: null,
    isLoading: false,
    error: null,
  },
  [CUSTOM_ORDER_HISTORY_OVERVIEW_FILTERS]: {
    customOrderHistoryRequest: {
      orderDateFrom: '',
      orderDateTo: '',
      productCode: '',
      orderId: '',
      type: '',
      sortDirection: '',
      sortOption: '',
    },
    backToOverview: false,
  },
  [CUSTOM_ORDER_HISTORY]: {
    order: null,
    isLoading: false,
    error: null,
  },
  [CUSTOM_ORDER_HISTORY_CANCEL_ORDER]: {
    isRunning: false,
    success: false,
    error: false,
  },
  [CUSTOM_ORDER_HISTORY_CANCEL_ORDER_ENTRY]: {
    isRunning: false,
    success: false,
    error: false,
  },
  [CUSTOM_ORDER_HISTORY_UPDATE_ORDER_ENTRIES]: {
    isRunning: false,
    success: false,
    error: false,
  },
};

//******************************************************

export const customOrderHistoryReducer = createReducer(
  initialState,
  on(CustomOrderHistoryActions.getOrderHistoryOverview, (state,action) => ({
    ...state,
    [CUSTOM_ORDER_HISTORY_OVERVIEW]: {
      overview: null,
      isLoading: false,
      error: null,
    },
    [CUSTOM_ORDER_HISTORY_OVERVIEW_FILTERS]: {
      customOrderHistoryRequest: action.formData
    },
  })),
  on(
    CustomOrderHistoryActions.getOrderHistoryOverviewSuccess,
    (state, action) => ({
      ...state,
      [CUSTOM_ORDER_HISTORY_OVERVIEW]: {
        overview: action.overview,
        isLoading: true,
        error: null,
      },
    })
  ),
  on(
    CustomOrderHistoryActions.getOrderHistoryOverviewFailure,
    (state, action) => ({
      ...state,
      [CUSTOM_ORDER_HISTORY_OVERVIEW]: {
        overview: null,
        isLoading: false,
        error: action.error,
      },
    })
  ),
  //******************************************************
  on(CustomOrderHistoryActions.getOrderHistory, (state) => ({
    ...state,
    [CUSTOM_ORDER_HISTORY]: {
      order: null,
      isLoading: false,
      error: null,
    },
  })),
  on(CustomOrderHistoryActions.getOrderHistorySuccess, (state, action) => ({
    ...state,
    [CUSTOM_ORDER_HISTORY]: {
      order: action.order,
      isLoading: true,
      error: null,
    },
  })),
  on(CustomOrderHistoryActions.getOrderHistoryFailure, (state, action) => ({
    ...state,
    [CUSTOM_ORDER_HISTORY]: {
      order: null,
      isLoading: false,
      error: action.error,
    },
  })),
    //******************************************************
    on(CustomOrderHistoryActions.sortOrderHistory,
      (state, action) => ({
        ...state,
        [CUSTOM_ORDER_HISTORY_OVERVIEW_FILTERS]: {
          customOrderHistoryRequest: action.customOrderHistorySortData
        },
      })
    ),
    on(CustomOrderHistoryActions.backToOrderHistory,
      (state) => ({
        ...state,
        [CUSTOM_ORDER_HISTORY_OVERVIEW_FILTERS]: {
          customOrderHistoryRequest: state[CUSTOM_ORDER_HISTORY_OVERVIEW_FILTERS].customOrderHistoryRequest,
          backToOverview: true,
        },
      })
    ),
    on(CustomOrderHistoryActions.resetBackToOrderHistory,
      (state) => ({
        ...state,
        [CUSTOM_ORDER_HISTORY_OVERVIEW_FILTERS]: {
          backToOverview: false,
        },
      })
    ),
  //******************************************************
  on(CustomOrderHistoryActions.clearOrderHistoryOverview, (state, action) => ({
    ...state,
    [CUSTOM_ORDER_HISTORY_OVERVIEW]: {
      overview: null,
      isLoading: false,
      error: null,
    },
  })),
  //******************************************************
  on(CustomOrderHistoryActions.cancelOrder, (state) => ({
    ...state,
    [CUSTOM_ORDER_HISTORY_CANCEL_ORDER]: {
      isRunning: true,
      success: false,
      error: false,
    },
  })),
  on(CustomOrderHistoryActions.cancelOrderSuccess, (state, action) => ({
    ...state,
    [CUSTOM_ORDER_HISTORY_CANCEL_ORDER]: {
      isRunning: false,
      success: true,
      error: false,
    },
  })),
  on(CustomOrderHistoryActions.cancelOrderFailure, (state, action) => ({
    ...state,
    [CUSTOM_ORDER_HISTORY_CANCEL_ORDER]: {
      isRunning: false,
      success: false,
      error: true,
    },
  })),
  on(CustomOrderHistoryActions.cancelOrderReset, (state, action) => ({
    ...state,
    [CUSTOM_ORDER_HISTORY_CANCEL_ORDER]: {
      isRunning: false,
      success: false,
      error: false,
    },
  })),
  //******************************************************
  on(CustomOrderHistoryActions.cancelOrderEntry, (state) => ({
    ...state,
    [CUSTOM_ORDER_HISTORY_CANCEL_ORDER_ENTRY]: {
      isRunning: true,
      success: false,
      error: false,
    },
  })),
  on(CustomOrderHistoryActions.cancelOrderEntrySuccess, (state, action) => ({
    ...state,
    [CUSTOM_ORDER_HISTORY_CANCEL_ORDER_ENTRY]: {
      isRunning: false,
      success: true,
      error: false,
    },
  })),
  on(CustomOrderHistoryActions.cancelOrderEntryFailure, (state, action) => ({
    ...state,
    [CUSTOM_ORDER_HISTORY_CANCEL_ORDER_ENTRY]: {
      isRunning: false,
      success: false,
      error: true,
    },
  })),
  on(CustomOrderHistoryActions.cancelOrderEntryReset, (state, action) => ({
    ...state,
    [CUSTOM_ORDER_HISTORY_CANCEL_ORDER_ENTRY]: {
      isRunning: false,
      success: false,
      error: false,
    },
  })),
  //******************************************************
  on(CustomOrderHistoryActions.updateOrderEntries, (state) => ({
    ...state,
    [CUSTOM_ORDER_HISTORY_UPDATE_ORDER_ENTRIES]: {
      isRunning: true,
      success: false,
      error: false,
    },
  })),
  on(CustomOrderHistoryActions.updateOrderEntriesSuccess, (state, action) => ({
    ...state,
    [CUSTOM_ORDER_HISTORY_UPDATE_ORDER_ENTRIES]: {
      isRunning: false,
      success: true,
      error: false,
    },
  })),
  on(CustomOrderHistoryActions.updateOrderEntriesFailure, (state, action) => ({
    ...state,
    [CUSTOM_ORDER_HISTORY_UPDATE_ORDER_ENTRIES]: {
      isRunning: false,
      success: false,
      error: true,
    },
  })),
  on(CustomOrderHistoryActions.updateOrderEntriesReset, (state, action) => ({
    ...state,
    [CUSTOM_ORDER_HISTORY_UPDATE_ORDER_ENTRIES]: {
      isRunning: false,
      success: false,
      error: false,
    },
  }))
);
